import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { writingAPI, writingResultsAPI } from '../services/apiServices';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';
import moment from 'moment';
import styles from './WritingDashboard.module.css';

const WritingDashboard = () => {
  const { userProgress } = useUser();
  const navigate = useNavigate();
  const [tests, setTests] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('newtest');

  useEffect(() => {
    fetchData();
  }, []);

  const modes = [
    {
      title: 'Short Story',
      description: 'Spot and correct errors in short stories',
      icon: '📖',
      level:  1,
      mode: 'correction',
      path: 'short-story'
    },
    {
      title: 'Short Sentences',
      description: 'Find and fix mistakes in sentences to make them correct',
      icon: '❓',
      level:  1,
      mode: 'construction',
      path: 'short-sentences'
    },
    {
      title: 'Creative Writing',
      description: 'Use your imagination to create fun and interesting stories',
      icon: '✏️',
      level:  1,
      mode: 'story',
      path: 'creative-writing'
    },
    {
      title: 'Persuasive Writing',
      description: 'Learn how to make strong points that convince others',
      icon: '🔍',
      level:  1,
      mode: 'adaptive',
      path: 'persuasive-writing'
    }
  ]

  const fetchData = async () => {
    try {
      const [sessionsData, resultsData] = await Promise.all([
        writingAPI.getActiveSessions(),
        writingResultsAPI.getUserResults()
      ]);

      setTests(sessionsData);
      setResults(resultsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTestClick = (mode) => {
    navigate(`/writely/mode/${mode.path}`, {state: {title: mode.title}});
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
  };

  // Calculate statistics from results
  const recentResults = results || [];
  const averageScore = recentResults.length 
    ? Math.round(recentResults.reduce((acc, curr) => acc + curr.totalScore, 0) / recentResults.length)
    : 0;
  const latestGrade = recentResults.length > 0 
    ? recentResults[0]?.recommendedGrade 
    : 0;

  // Define stats for stats cards
  const stats = [
    {
      label: 'Current Grade',
      value: userProgress?.overallLevel || 1,
      icon: '🎯',
      iconClass: styles.iconBlue
    },
    {
      label: 'Average Score',
      value: `${averageScore}%`,
      icon: '📊',
      iconClass: styles.iconGreen
    },
    {
      label: 'Latest Grade',
      value: latestGrade,
      icon: '📝',
      iconClass: styles.iconPurple
    },
    {
      label: 'Total Tests',
      value: recentResults.length > 0 ? recentResults.length : 0,
      icon: '📚',
      iconClass: styles.iconYellow
    }
  ];

  const renderNewTest = () => (
    <div className={styles.modesGrid}>
      {modes.map((mode, index) => (
        <div key={index} className={styles.modeCard}>
          <div className={styles.modeHeader}>
            <span className={styles.modeIcon}>{mode.icon}</span>
            <div className={styles.modeTitleContainer}>
              <h3 className={styles.modeTitle}>{mode.title}</h3>
              <div className={styles.levelBadge}>Level {mode.level}</div>
            </div>
          </div>
          <p className={styles.modeDescription}>{mode.description}</p>
          <div className={styles.difficultyOptions}>
            <button
              className={styles.startButton}
              onClick={() => handleStartTestClick(mode)}
            >
             Start New Test
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderResults = () => (
    <div className={styles.modesGrid}>
      {results && results.length > 0 ? (
        results.map((result) => (
          <div key={result._id} className={styles.modeCard}>
            <div className={styles.modeHeader}>
              <span className={styles.modeIcon}>📝</span>
              <div className={styles.modeTitleContainer}>
                <h3 className={styles.modeTitle}>Grade {result.testSession.gradeWhenStart}</h3>
                <div className={`${styles.levelBadge} ${result.totalScore >= 70 ? styles.badgeSuccess : result.totalScore >= 40 ? styles.badgeWarning : styles.badgeError}`}>
                  {Math.round(result.totalScore)}%
                </div>
              </div>
            </div>
            <div className={styles.testDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>⏱️</span>
                <span className={styles.detailText}>{Math.floor(result.timeTaken / 60)}m {result.timeTaken % 60}s</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>🎓</span>
                <span className={styles.detailText}>Grade {result.recommendedGrade}</span>
              </div>
            </div>
            <p className={styles.modeDescription}>Completed {moment(result.completedAt).fromNow()}</p>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${result.totalScore}%` }}
              />
            </div>
            <button 
              className={styles.startButton}
              onClick={() => navigate(`/writing-results/${result._id}`)}
            >
              View Details
            </button>
          </div>
        ))
      ) : (
        <div className={styles.emptyState}>
          <p>No results available yet</p>
          <button
            className={styles.startButton}
            onClick={handleStartTestClick}
          >
            Start Your First Test
          </button>
        </div>
      )}
    </div>
  );

  const renderAvailableTests = () => (
    <div className={styles.modesGrid}>
      {tests && tests.length > 0 ? (
        tests.map((test) => (
          <div key={test._id} className={styles.modeCard}>
            <div className={styles.modeHeader}>
              <span className={styles.modeIcon}>✍️</span>
              <div className={styles.modeTitleContainer}>
                <h3 className={styles.modeTitle}>Grade {test.gradeWhenStart}</h3>
                <div className={styles.levelBadge}>
                  {test.totalQuestions} Questions
                </div>
              </div>
            </div>
            <div className={styles.testDetails}>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>⏱️</span>
                <span className={styles.detailText}>{moment(test.sessionStart).fromNow()}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailIcon}>📊</span>
                <span className={styles.detailText}>{test.totalAnswers}/{test.totalQuestions}</span>
              </div>
            </div>
            <p className={styles.progressLabel}>Progress</p>
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${(test.totalAnswers / test.totalQuestions) * 100}%` }}
              />
            </div>
            <button 
              className={styles.startButton}
              onClick={() => navigate(`/writing-test/${test.sessionId}`)}
            >
              Continue Test
            </button>
          </div>
        ))
      ) : (
        <div className={styles.emptyState}>
          <p>No active tests available</p>
          <button
            className={styles.startButton}
            onClick={handleStartTestClick}
          >
            Start a New Test
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Writely</h1>
      </div>

      <div className={styles.statsGrid}>
        {stats.map((stat, index) => (
          <div key={index} className={styles.statCard}>
            <div className={styles.statContent}>
              <div className={`${styles.statIcon} ${stat.iconClass}`}>
                {stat.icon}
              </div>
              <div>
                <p className={styles.statLabel}>{stat.label}</p>
                <p className={styles.statValue}>{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.tabs}>
        <div className={styles.tabList}>
          <button
            className={`${styles.tabButton} ${activeTab === 'newtest' ? styles.active : ''}`}
            onClick={() => handleTabChange('newtest')}
          >
            Start New Test
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'result' ? styles.active : ''}`}
            onClick={() => handleTabChange('result')}
          >
            Recent Results
          </button>
          <button
            className={`${styles.tabButton} ${activeTab === 'availableTest' ? styles.active : ''}`}
            onClick={() => handleTabChange('availableTest')}
          >
            Available Tests
          </button>
        </div>

        {activeTab === 'newtest' && renderNewTest()}
        {activeTab === 'result' && renderResults()}
        {activeTab === 'availableTest' && renderAvailableTests()}
      </div>

    </div>
  );
};

export default WritingDashboard;